import react, { useCallback, useEffect, useMemo, useState } from "react";
import portViewService from "../../services/portView.service";
import ManagePortsService from "../../services/managePorts.service";
import { throwGenericToasterError, toasterSuccess } from "../../utils/methods";
import { editNRPayload } from "../../interfaces/managePorts.interface";
import useMoveToWaveXchange, { moveToWaveXchangeType } from "./moveToWaveXchangeHook";
import useCancelSubscription, { cancelSubscriptionType } from "./useCancelService";
import { toasterError } from "../../utils/methods";
import useRollService, { rollServiceType } from "./useRollService";
import { useForSalePortHook } from "../ManagePorts/Table/PutUpForSale/useForSalePortHook";
import useDeleteOffNet from "./useDeleteOffNetHook";
import useManageLeasedPorts from "./useManageLeasedPorts";
import { useGroup } from "../ManagePorts/GroupOperations/useGroup";
import useMoveToMMR from "./moveToMMRHook";

export interface PORT_VIEW {
  nr_id: number;
  ne_id: number;
  node_id: number;
  parent_id: number;
  listing_id: number | null;
  buyer_id: number | null;
  buyer_organization: string | null;
  bandwidth_id: number | null;
  organization_id: number;
  leased_organization: number | null;
  cross_connect_port_id: number | null;
  port_description: string;
  port_name: string;
  port_type: string;
  group_id: number | null;
  group_name: string | null;
  verified: boolean;
  bandwidth_port_id: number | null;
  requested_verification: boolean;
  ne_name: string;
  ne_on_net: boolean;
  ne_leased_organization: number | null;
  bandwidth_description: string | null;
  request_move_to_mmr: boolean;
  bandwidth_name: string | null;
  bandwidth_verified: boolean | null;
  cross_connect_status: "green" | "grey" | "pending" | "none";
  service_description: string | null;
  on_contract_price: number | null;
  on_demand_price: number | null;
  type: string | null;
  terms_on_contract_url: string | null;
  terms_on_demand_url: string | null;
  position_in_ne: number | null;
  position_in_mmr: number | null;
}

export interface editPortsHook {
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
}

export interface editPortsInterface {
  resetEdits: () => void;
  sendEdits: () => void;
  isPortNameOrDescriptionChanged: boolean;
  loading: boolean;
}
export interface loggerListInterface {
  name: string;
  email: string;
  description: string;
  date: Date;
}
export interface candidateRollsInterface {
  id: number;
  name: string;
}

export interface PortHookInterface {
  portDetails: PORT_VIEW | null;
  loading: boolean;
  formHooks: editPortsHook;
  logs: loggerListInterface[];
  editPorts: editPortsInterface;
  candidateRolls: candidateRollsInterface[];
  refetchPort: () => void;
  moveToWaveXchange: ReturnType<typeof useMoveToWaveXchange>;
  moveToMMR: ReturnType<typeof useMoveToMMR>;
  cancelSubscription: ReturnType<typeof useCancelSubscription>;
  rollService: ReturnType<typeof useRollService>;
  listService: ReturnType<typeof useForSalePortHook>;
  deleteOffNet: ReturnType<typeof useDeleteOffNet>;
  canDeleteOffNet: boolean;
  manageLeasedPorts: ReturnType<typeof useManageLeasedPorts>;
  groupService: ReturnType<typeof useGroup>;
}

function usePortDetails(portId: number | undefined, isMounted: boolean, isOpen: boolean, setChangedTrue: () => void, refreshIndex: () => void) {
  const [portDetails, setPortDetails] = useState<PORT_VIEW | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [portName, setPortName] = useState("");
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<loggerListInterface[]>([]);
  const [candidateRolls, setCandidateRolls] = useState<candidateRollsInterface[]>([]);
  const [listingLoading, setListingLoading] = useState(false);
  const groupService = useGroup(String(portDetails?.ne_id), { isMounted, isBandwidth: !!portDetails?.bandwidth_id, lowerDrawer: true }, null, portDetails);
  const groupId = useMemo(() => groupService.GroupDropdownProps.groupId, [groupService && groupService.GroupDropdownProps.groupId]);

  const portHooks: editPortsHook = useMemo(() => {
    return {
      name: portName,
      setName: setPortName,
    };
  }, [portName, groupId]);

  useEffect(() => {
    if (!isOpen) return;
    if (typeof portId === "number") fetchPort(portId, isMounted);
  }, [isOpen, portId]);

  const refetch = useCallback(() => {
    if (!(typeof portId === "number")) return;
    fetchPort(portId, isMounted);
    setChangedTrue();
  }, [portId, isMounted]);

  const fetchPort = useCallback(
    (portId: number | undefined, isMounted: boolean) => {
      if (!portId) return;
      setLoading(true);
      portViewService
        .getPortView(portId)
        .then((result: any) => {
          if (!isMounted) return;
          const details = result.data.details as PORT_VIEW;
          setLogs(result.data.logs as loggerListInterface[]);
          setCandidateRolls(
            details.bandwidth_id
              ? (result.data.candidateRollsBandwidth as candidateRollsInterface[])
              : (result.data.candidateRolls as candidateRollsInterface[])
          );
          setPortDetails(details);
          setPortName(details.port_name);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          throwGenericToasterError(e);
        });
    },
    [portId]
  );

  const isPortNameOrDescriptionChanged = useMemo(() => {
    return (!!portName && portName !== portDetails?.port_name) || (!!groupId && groupId !== portDetails?.group_id);
  }, [portName, portDetails, groupId]);

  const sendEdits = useCallback(() => {
    setEditLoading(true);
    if (!portDetails || !groupId) return;
    const payload: editNRPayload = {
      nrId: portDetails.nr_id,
      neId: portDetails.ne_id.toString(),
      groupId: groupId,
      portName: portName,
    };
    ManagePortsService.editNR(payload)
      .then(() => {
        toasterSuccess("Port Name and Description Saved Successfully.");
        if (!isMounted) return;
        setEditLoading(false);
        refetch();
      })
      .catch((err) => {
        if (!isMounted) return;
        setEditLoading(false);
        toasterError(err.message);
      });
  }, [portDetails, groupId, portName, refetch]);

  const resetEdits = useCallback(() => {
    if (!portDetails) return;
    groupService.GroupDropdownProps.reset(portDetails.group_id);
    setPortName(portDetails.port_name);
  }, [portDetails, groupService]);

  const editPorts: editPortsInterface = useMemo(() => {
    return { resetEdits, sendEdits, isPortNameOrDescriptionChanged, loading: editLoading };
  }, [resetEdits, sendEdits, isPortNameOrDescriptionChanged, editLoading]);

  const moveToWaveXchange = useMoveToWaveXchange(portId, fetchPort, isMounted, refetch);
  const cancelSubscription = useCancelSubscription(portId, isMounted, refreshIndex);
  const deleteOffNet = useDeleteOffNet(portId, isMounted, refreshIndex);
  const manageLeasedPorts = useManageLeasedPorts(portId, isMounted, refreshIndex);
  const moveToMMR = useMoveToMMR(portId, fetchPort, isMounted, refetch);

  const canDeleteOffNet = useMemo(
    () =>
      !portDetails?.parent_id &&
      !portDetails?.verified &&
      !portDetails?.requested_verification &&
      !portDetails?.buyer_id &&
      !portDetails?.cross_connect_port_id &&
      !portDetails?.listing_id,
    [portDetails]
  );
  const rollService = useRollService(portId, isMounted, refetch);
  const listService = useForSalePortHook(isMounted, refetch, listingLoading, setListingLoading, true);

  return {
    portDetails,
    loading,
    formHooks: portHooks,
    logs,
    editPorts,
    moveToWaveXchange,
    moveToMMR,
    cancelSubscription,
    refetchPort: refetch,
    candidateRolls,
    rollService,
    listService,
    deleteOffNet,
    canDeleteOffNet,
    manageLeasedPorts,
    groupService,
  } as unknown as PortHookInterface;
}

export default usePortDetails;
